import { CdkDragDrop, CdkDragEnter, CdkDragExit, CdkDropList } from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { AvmFileCounterPipe } from '@hub4b/pipes/avm-file-counter.pipe';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BoxInfos } from 'src/app/models/box-infos';
import { DragContext } from 'src/app/models/drag-context';
import { BoxSelectionEvent } from 'src/app/models/events/box-selection-event';
import { Folder } from 'src/app/models/server/folder';
import { FolderActionsService } from 'src/app/services/actions/folder-actions.service';
import { BoxCounterEventsService } from 'src/app/services/events/box-counter-events.service';
import { BoxesStateService } from 'src/app/services/state/boxes-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';

@Component({
    selector: 'app-box',
    templateUrl: './box.component.html',
    styleUrls: ['./box.component.scss'],
    imports: [CdkDropList, MatCheckboxModule, FormsModule, AvmFileCounterPipe]
})
export class BoxComponent implements OnDestroy {
    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Informations sur la bannette affichée.
     */
    @Input()
    public datasource: BoxInfos;

    /**
     * Emis lorsque cette bannette est selectionnée ou déselectionnée.
     */
    @Output()
    public selected: EventEmitter<BoxSelectionEvent> = new EventEmitter<BoxSelectionEvent>();

    /**
     * Indique si une opération "déposer" (drop) est permise sur ce conteneur en fonction de l'élement glissé (drag)
     */
    dropAllowed: boolean = false;

    /**
     * Valeur indiquant si le composant doit émettre des événements. False : n'émet pas d'événement.
     */
    @Input()
    quiet: boolean;

    /**
     * Constructeur.
     * @param document : Element du DOM representant le document.
     * @param rootElement : Element racine du DOM correspondant à ce composant.
     */
    constructor(
        @Inject(DOCUMENT) private document,
        private rootElement: ElementRef,
        private boxesStateService: BoxesStateService,
        private folderActionsService: FolderActionsService,
        private boxCounterEventsService: BoxCounterEventsService,
        private viewsStateService: ViewsStateService
    ) {
        this.boxesStateService.onBoxCounterChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((x) => {
            if (!!x && !!this.datasource && x.id === this.datasource.id) {
                this.datasource.count = x.count;
            }
        });

        this.boxCounterEventsService.counterUpdated.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            if (!!event && !!this.datasource && event.boxId === this.datasource.id) {
                //console.warn(`Réception de l'événement BoxCounterUpdatedEvent par le composant BoxComponent de la bannette ${this.datasource.id}`, event);
                this.datasource.count = event.count;
            }
        });
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Levé lorsque l'utilisateur coche ou décoche cette bannette.
     * @param event : Événement coché ou décoché.
     */
    public onChanged(event: MatCheckboxChange) {
        this.datasource.selected = event.checked;
        if (!this.quiet) {
            this.selected.emit(new BoxSelectionEvent(this.datasource.id, event.checked));
        }
    }

    /**
     * Levé lorsque une opération de déposer (drop) se produit sur ce composant.
     * Attention, si le dernier conteneur de drop survolé est celui-ci, l'événement sera également levé si le drop se fait n'importe où sur le document.
     * @param event : Événement de déposer (drop)
     */
    onListDrop(event: CdkDragDrop<Folder>): void {
        this.dropAllowed = false;

        if (Number.isNaN(event.dropPoint.x) && Number.isNaN(event.dropPoint.y)) {
            // Cas d'un drag&drop annulé par la touche ECHAP
            (event.item.data.source as DragContext<Folder>).clearDragAndDropContext();
            return;
        }

        // Détermine l'élément du DOM sur lequel l'opération de déposer (drop) s'est produite.
        const elm = document.elementFromPoint(event.item.data.position.x, event.item.data.position.y);

        // Détermine si l'opération de déposer (drop) s'est produite en dehors du composant.
        const isDropOutsideComponent = !this.rootElement.nativeElement.contains(elm);

        if (event.item.data.type === 'Folder' && !isDropOutsideComponent) {
            // Si l'élément "déposé" est de type Folder et que l'événement déposé s'est bien produit au dessus de cette bannette --> Déplacement dans la bannette.

            // Itère sur chaque dossier sélectionné et le déplace dans la bannette destination.
            event.item.data['folders'].forEach((folder) => {
                this.folderActionsService.moveToBox(this.viewsStateService.getSelectedView().id, folder, this.datasource.id);
            });
        } else {
            (event.item.data.source as DragContext<Folder>).clearDragAndDropContext();
        }
    }

    /**
     * Levé lorsque l'utilisateur déplace un élément dans ce conteneur.
     * @param event : Événement de glisser (drag).
     */
    onDropListEntered(event: CdkDragEnter<Folder>): void {
        this.dropAllowed = true;
    }

    /**
     * Levé lorsque l'utilisateur déplace un élément en dehors de ce conteneur.
     * Attention, cet événement n'est émis que si l'élément est déplacer dans un autre conteneur drop.
     * @param event : Événement de glisser (drag).
     */
    onDropListExited(event: CdkDragExit<Folder>): void {
        this.dropAllowed = false;
    }
}
